import { RADashboardCardId, RADashboardCardSize } from '@/constants/ReportDashboard/cards-enums'
import { lookupCardSize } from '@/constants/ReportDashboard/cards-sizes'
import { AttributionHoldingsProvider } from '@/contexts/AttributionHoldingsContext'
import FactorLensProvider from '@/contexts/FactorLensContext'
import { HoldingsProvider } from '@/contexts/HoldingsTabContext'
import { useCardsInformation } from '@/contexts/RADashboardLayoutContext/hooks'
import {
  CardDimension,
  CardSettings,
  RADashboardGrid,
} from '@/interfaces/raDashboard.interface'
import useElementDimensions from '@/utils/useElementDimensions'
import { Box } from '@mui/material'
import React from 'react'
import { CardRenderer } from './CardRenderer'

const DashboardCardCanvas: React.FC<RADashboardGrid> = (props) => {
  const { cardPrefs, onCardPrefsChange } = props
  const cardsInfo = useCardsInformation()

  //Vars for making sure the grid is responsive to screen resizing
  const { dimensions, dimensionsRef } = useElementDimensions()
  const { width } = dimensions ?? {}
  const canvasPadding = 50
  const maxGridWidth = 12
  const gridUnitSize = width ? (width - canvasPadding) / maxGridWidth : 100

  // Responsive breakpoints based on container width
  const isNarrow = width !== undefined && width < 500
  const isMedium = width !== undefined && width >= 500 && width < 900

  const handleCardDimensions = (
    cardKey: string,
    cardSettings: CardSettings,
  ) => {
    let cardDimensions = null

    cardDimensions = lookupCardSize(
      cardKey,
      cardSettings?.size ?? RADashboardCardSize.Medium,
    )

    if (isNarrow || isMedium) {
      if (cardKey.startsWith('empty_')) {
        cardDimensions = null
      } else {
        cardDimensions = scaleCardForResponsiveness(cardDimensions)
      }
    }

    return cardDimensions
  }

  const scaleCardForResponsiveness = (dimensions: CardDimension | null) => {
    if (dimensions == null) {
      return null
    }

    let newWidth = dimensions.width
    if (isNarrow) {
      newWidth *= 3
    } else if (isMedium) {
      newWidth *= 2
    }

    return { ...dimensions, width: Math.min(newWidth, maxGridWidth) }
  }

  const getLayoutForCurrentTab = () => {
    return cardsInfo.layouts[props.selectedTab] ?? null
  }

  const getSettingsForCard = (cardKey: string) => {
    return cardsInfo.settings[cardKey] ?? null
  }

  const gridContainerStyle = (gridWidth: number) => {
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${gridWidth}, 1fr)`,
      gridAutoRows: `${gridUnitSize}px`,
      gap: '8px',
    }
  }

  const gridItemStyle = (size: CardDimension) => {
    return {
      gridColumn: `span ${size.width}`,
      gridRow: `span ${size.height}`,
    }
  }

  return (
    <div ref={dimensionsRef} style={{ width: '100%', marginBottom: 20 }}>
      <FactorLensProvider>
        <HoldingsProvider>
        <AttributionHoldingsProvider>
          <Box sx={gridContainerStyle(maxGridWidth)}>
            {getLayoutForCurrentTab()?.cardsOrder?.map((cardKey, index) => {
              const cardSettings = getSettingsForCard(cardKey)

              let cardDimensions = handleCardDimensions(cardKey, cardSettings)
              if (cardDimensions == null) return null

              const cardProps = {
                key: cardKey,
                cardId: cardKey as RADashboardCardId,
                w: cardDimensions.width,
                h: cardDimensions.height,
                gridUnitSize: gridUnitSize,
                cardSettings: cardSettings,
                cardPrefs,
                onCardPrefsChange,
              }

              return (
                <Box
                  sx={gridItemStyle(cardDimensions)}
                  key={index + cardProps.key}
                >
                  <CardRenderer cardProps={cardProps} dashboardType={props.dashboardType} />
                </Box>
              )
            })}
          </Box>
        </AttributionHoldingsProvider>
        </HoldingsProvider>
      </FactorLensProvider>
    </div>
  )
}

export default DashboardCardCanvas
